/**
 * UranIA PA — API client onboarding PA (US-015 AC#3).
 *
 * Wrapper su `fetch` per `services/api-gateway` (OAS v0.4.0):
 *   - `POST /api/v1/onboarding/pa/sessions`              -> apre/idempot.
 *   - `POST /api/v1/onboarding/pa/sessions/{id}/steps/{n}` -> persist step.
 *
 * Auth (US-015 AC#3 + US-010): bearer JWT preso dallo Zustand
 * `useAuthStore.accessToken` (refresh silente via Keycloak SSO cookie,
 * niente refresh_token client-side per FR-F1-10).
 *
 * Errori:
 *   - rete / 5xx -> `OnboardingApiError` con codice `network_error`;
 *   - 4xx Problem+JSON RFC 9457 -> `OnboardingApiError` con `status` +
 *     `code` (campo OAS `Problem.code` enum: `session_not_found`,
 *     `tenant_mismatch`, `step_out_of_order`, `consent_missing`, ...).
 *
 * Tipi di ritorno restano "raw" DTO (struct con campi snake_case del
 * server). Conversione a camelCase eventuale e' lasciata ai consumer.
 */

import { loadSpaEnv } from '../env.ts';
import { useAuthStore } from '../auth/auth-store.ts';
import type {
  OnboardingPaSessionDto,
  OnboardingPaStep1Body,
  OnboardingPaStep2Body,
  OnboardingPaStep3Body,
  OnboardingPaStep4Body,
  OnboardingPaStep5Body,
  OnboardingPaStepNumber,
  OnboardingPaStepResponseDto,
} from './pa-schemas.ts';

export type OnboardingPaStepBody =
  | OnboardingPaStep1Body
  | OnboardingPaStep2Body
  | OnboardingPaStep3Body
  | OnboardingPaStep4Body
  | OnboardingPaStep5Body;

export interface OnboardingApiErrorPayload {
  readonly status: number;
  readonly code: string | null;
  readonly title: string;
  readonly detail: string | null;
  readonly acrRequired?: readonly string[] | null;
}

export class OnboardingApiError extends Error {
  public readonly status: number;
  public readonly code: string | null;
  public readonly detail: string | null;
  public readonly acrRequired: readonly string[] | null;

  constructor(payload: OnboardingApiErrorPayload) {
    super(payload.title);
    this.name = 'OnboardingApiError';
    this.status = payload.status;
    this.code = payload.code;
    this.detail = payload.detail;
    this.acrRequired = payload.acrRequired ?? null;
  }
}

function authHeader(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  if (token === null || token.length === 0) {
    return {};
  }
  return { Authorization: `Bearer ${token}` };
}

function buildUrl(path: string): string {
  const env = loadSpaEnv();
  const base = env.apiBaseUrl.replace(/\/+$/u, '');
  return `${base}${path}`;
}

interface ProblemBody {
  readonly title?: unknown;
  readonly detail?: unknown;
  readonly status?: unknown;
  readonly code?: unknown;
  readonly acr_required?: unknown;
}

function readStringArray(value: unknown): readonly string[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  return value.filter((v): v is string => typeof v === 'string');
}

async function parseProblem(response: Response): Promise<OnboardingApiError> {
  let body: ProblemBody = {};
  try {
    body = (await response.json()) as ProblemBody;
  } catch {
    // Server non ha emesso Problem+JSON valido: fallback su statusText.
  }
  const title =
    typeof body.title === 'string' && body.title.length > 0
      ? body.title
      : response.statusText || 'http_error';
  const detail = typeof body.detail === 'string' ? body.detail : null;
  const code = typeof body.code === 'string' ? body.code : null;
  return new OnboardingApiError({
    status: response.status,
    code,
    title,
    detail,
    acrRequired: readStringArray(body.acr_required),
  });
}

async function postJson(
  path: string,
  body: unknown,
): Promise<{ readonly response: unknown; readonly status: number }> {
  let response: Response;
  const init: RequestInit = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...authHeader(),
    },
    credentials: 'include',
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  try {
    response = await fetch(buildUrl(path), init);
  } catch (err) {
    throw new OnboardingApiError({
      status: 0,
      code: 'network_error',
      title: err instanceof Error ? err.message : 'network_error',
      detail: null,
    });
  }
  if (!response.ok) {
    throw await parseProblem(response);
  }
  const parsed: unknown = await response.json();
  return { response: parsed, status: response.status };
}

export async function createOnboardingPaSession(): Promise<{
  readonly session: OnboardingPaSessionDto;
  readonly status: 200 | 201;
}> {
  const { response, status } = await postJson('/api/v1/onboarding/pa/sessions', undefined);
  return { session: response as OnboardingPaSessionDto, status: status === 200 ? 200 : 201 };
}

export async function submitOnboardingPaStep(
  sessionId: string,
  stepNumber: OnboardingPaStepNumber,
  body: OnboardingPaStepBody,
): Promise<OnboardingPaStepResponseDto> {
  const path = `/api/v1/onboarding/pa/sessions/${encodeURIComponent(sessionId)}/steps/${String(
    stepNumber,
  )}`;
  const { response } = await postJson(path, body);
  return response as OnboardingPaStepResponseDto;
}
